// PDF generation service for estimates and invoices
// Styled PDF (Email + download): same HTML+CSS as Print via buildInvoiceHtml + html2pdf.
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import { getLetterTemplate, getDefaultLetterTemplates } from './letterTemplates';
import { buildInvoiceHtml } from '../utils/documentRenderer';
import { renderTemplate } from '../utils/templateRenderer';
import { assembleDocument } from '../utils/assembleDocument';
import { getEnableDueDates } from './settings';
import { supabase } from '../lib/supabase';

/**
 * Generate a styled PDF from the canonical document HTML + inlined CSS (matches Print output).
 * Used for Email and download so PDF is not unstyled.
 */
export const generateStyledPDF = async (document, client, type = 'invoice') => {
  const templateType = type === 'estimate' ? 'estimate' : 'invoice';
  let template;
  try {
    template = await getLetterTemplate(templateType);
  } catch (error) {
    console.log('Using default letter template:', error);
    const defaults = getDefaultLetterTemplates();
    template = defaults[templateType];
  }

  const { html, css } = await buildInvoiceHtml(document, client, template, {
    type: templateType,
    hideTotals: document.hide_totals ?? false,
    mode: 'email',
  });

  const fullHtml = `<style>${css}</style><div class="print-view"><div class="print-template-content">${html}</div></div>`;
  if (typeof process !== 'undefined' && process.env?.DEBUG_PDF === '1') {
    console.debug('[PDF] Using canonical buildInvoiceHtml for email/download. CSS inlined:', css.length, 'chars. HTML length:', html.length);
  }
  const opt = {
    margin: 10,
    filename: `${type}_${document.invoice_number || document.id}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, letterRendering: true },
    jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
  };

  await html2pdf().set(opt).from(fullHtml).save();
};

// Legacy jsPDF-based generator (no CSS); kept for reference or fallback. Prefer generateStyledPDF.
export const generatePDF = async (document, client, type = 'invoice') => {
  let template;
  try {
    const templateType = type === 'estimate' ? 'estimate' : 'invoice';
    template = await getLetterTemplate(templateType);
  } catch (error) {
    console.log('Using default letter template:', error);
    const defaults = getDefaultLetterTemplates();
    template = defaults[type === 'estimate' ? 'estimate' : 'invoice'];
  }
  return await generatePDFFromTemplate(document, client, type, template);
};

const generatePDFFromTemplate = async (document, client, type, template) => {
  // UNIFIED RENDERING: Use renderTemplate to get fully rendered HTML first
  // This ensures PDF uses the exact same output as Print and Email
  const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ') || '';
  const invoiceDate = new Date(document.date).toLocaleDateString();
  const invoiceNumber = document.invoice_number || document.id;
  const items = typeof document.items === 'string' ? JSON.parse(document.items) : document.items;
  
  // Get enable due dates setting
  let enableDueDates = true;
  try {
    enableDueDates = await getEnableDueDates();
  } catch (error) {
    console.log('Could not load due date setting, defaulting to enabled');
  }

  // Only include due date if enabled
  const dueDate = enableDueDates && document.due_date 
    ? new Date(document.due_date).toLocaleDateString() 
    : '';

  let userName = '';
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    userName = authUser?.user_metadata?.full_name || authUser?.email || '';
  } catch {
    // User not available, leave empty
  }

  // Prepare template data for unified renderTemplate
  const templateData = {
    document_type: type.toUpperCase(),
    client: {
      name: client.name || 'Client',
      address: client.address || '',
      city_state_zip: clientCityStateZip,
      email: client.email || '',
      phone: client.phone || '',
    },
    invoice: {
      number: invoiceNumber,
      date: invoiceDate,
      due_date: dueDate, // Empty string if disabled
      document_type: type.toUpperCase(),
      subtotal: `$${parseFloat(document.subtotal || 0).toFixed(2)}`,
      tax: `$${parseFloat(document.tax_amount || 0).toFixed(2)}`,
      tax_amount: `$${parseFloat(document.tax_amount || 0).toFixed(2)}`,
      total: `$${parseFloat(document.total || 0).toFixed(2)}`,
      tax_rate: document.tax_rate || 0,
      include_tax: document.include_tax !== false,
      taxable_subtotal: document.taxable_subtotal || 0,
      non_taxable_subtotal: document.non_taxable_subtotal || 0,
      line_items: items,
      items_table: items,
      notes: document.notes || '',
    },
    date: invoiceDate,
    due_date: dueDate, // Empty string if disabled
    facility: {
      name: 'Menlo Atherton Glass',
      address: 'PO Box 5214, Redwood City CA 94063',
    },
    user: {
      name: userName,
    },
    notes_section: document.notes || '',
    tax_rate: document.tax_rate || 0,
  };
  
  // UNIFIED PIPELINE: Assemble document then render
  // Step 1: Assemble document structure
  const assembledHTML = assembleDocument({
    header: template.header || '',
    body: template.body || '',
    footer: template.footer || ''
  });
  
  // Step 2: Render with real data using unified renderTemplate
  const renderedHtml = renderTemplate(assembledHTML, templateData, document.hide_totals);
  
  // Now convert the rendered HTML to PDF
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 12, isBold = false, align = 'left') => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line) => {
      if (yPos > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        yPos = margin;
      }
      
      if (align === 'center') {
        const textWidth = doc.getTextWidth(line);
        doc.text(line, (pageWidth - textWidth) / 2, yPos);
      } else if (align === 'right') {
        const textWidth = doc.getTextWidth(line);
        doc.text(line, pageWidth - margin - textWidth, yPos);
      } else {
        doc.text(line, margin, yPos);
      }
      yPos += fontSize * 0.5;
    });
    yPos += 5;
  };

  // Parse the rendered HTML and walk DOM in order so table/totals/notes are detected and rendered with correct spacing
  const parser = new DOMParser();
  const dom = parser.parseFromString(renderedHtml, 'text/html');
  const blocks = [];

  const walk = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === 'TABLE' && node.classList.contains('print-items-table')) {
        blocks.push({ type: 'table', element: node });
        return;
      }
      if (node.tagName === 'DIV') {
        if (node.classList.contains('print-totals')) {
          blocks.push({ type: 'totals', element: node });
          return;
        }
        if (node.classList.contains('print-notes')) {
          blocks.push({ type: 'notes', element: node });
          return;
        }
      }
      if (['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
        const text = node.textContent.trim();
        if (text) {
          const fontSize = node.tagName === 'H1' ? 20 : node.tagName === 'H2' ? 18 : 12;
          blocks.push({ type: 'text', text, isBold: node.tagName !== 'P', fontSize, align: 'left' });
        }
        return;
      }
      for (const child of node.childNodes) walk(child);
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) blocks.push({ type: 'text', text, isBold: false, fontSize: 12, align: 'left' });
    }
  };
  for (const child of dom.body.childNodes) walk(child);

  // Column positions for table (with spacing so headers don't run together)
  const colDesc = margin;
  const colQty = margin + 72;
  const colUnitPrice = margin + 88;
  const colTaxable = margin + 128;
  const colTotal = pageWidth - margin;

  blocks.forEach((block) => {
    if (block.type === 'table') {
      const table = block.element;
      const rows = table.querySelectorAll('tr');
      if (rows.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const headerCells = rows[0].querySelectorAll('th');
        if (headerCells.length >= 5) {
          doc.text(headerCells[0].textContent.trim() || 'Description', colDesc, yPos);
          doc.text(headerCells[1].textContent.trim() || 'Qty', colQty, yPos);
          doc.text(headerCells[2].textContent.trim() || 'Unit Price', colUnitPrice, yPos);
          doc.text(headerCells[3].textContent.trim() || 'Taxable', colTaxable, yPos);
          doc.text(headerCells[4].textContent.trim() || 'Total', colTotal, yPos, { align: 'right' });
          yPos += 8;
        } else {
          doc.text('Description', colDesc, yPos);
          doc.text('Qty', colQty, yPos);
          doc.text('Unit Price', colUnitPrice, yPos);
          doc.text('Taxable', colTaxable, yPos);
          doc.text('Total', colTotal, yPos, { align: 'right' });
          yPos += 8;
        }
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;

        doc.setFont('helvetica', 'normal');
        for (let i = 1; i < rows.length; i++) {
          const cells = rows[i].querySelectorAll('td');
          if (rows[i].classList.contains('item-separator-row')) {
            yPos += 4;
            continue;
          }
          if ((rows[i].classList.contains('item-section-subtotal-row') || rows[i].classList.contains('item-section-tax-row')) && cells.length >= 2) {
            if (yPos > doc.internal.pageSize.getHeight() - 40) {
              doc.addPage();
              yPos = margin;
            }
            doc.setFont('helvetica', 'bold');
            doc.text((cells[0].textContent || 'Section Total').trim(), colDesc, yPos);
            doc.text((cells[1].textContent || '$0.00').trim(), colTotal, yPos, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            yPos += 8;
            continue;
          }
          if (cells.length >= 5) {
            if (yPos > doc.internal.pageSize.getHeight() - 40) {
              doc.addPage();
              yPos = margin;
            }
            const description = doc.splitTextToSize((cells[0].textContent || '').trim(), 68);
            doc.text(description, colDesc, yPos);
            const descHeight = description.length * 5;
            doc.text((cells[1].textContent || '0').trim(), colQty, yPos);
            doc.text((cells[2].textContent || '$0.00').trim(), colUnitPrice, yPos);
            doc.text((cells[3].textContent || 'No').trim(), colTaxable, yPos);
            doc.text((cells[4].textContent || '$0.00').trim(), colTotal, yPos, { align: 'right' });
            yPos += Math.max(descHeight, 8);
          }
        }
        yPos += 5;
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      }
      return;
    }

    if (block.type === 'totals' && !document.hide_totals) {
      const totalsDiv = block.element;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const labelX = pageWidth - margin - 70;
      const valueX = pageWidth - margin;

      totalsDiv.querySelectorAll('.print-totals-row').forEach((row) => {
        const spans = row.querySelectorAll('span');
        if (spans.length >= 2) {
          const label = (spans[0].textContent || '').trim();
          const value = (spans[1].textContent || '').trim();
          const isTotal = row.classList.contains('print-total');
          if (isTotal) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
          }
          doc.text(label, labelX, yPos, { align: 'right' });
          doc.text(value, valueX, yPos, { align: 'right' });
          yPos += 7;
          if (isTotal) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
          }
        }
      });
      yPos += 3;
      return;
    }

    if (block.type === 'notes') {
      const notesDiv = block.element;
      yPos += 5;
      if (yPos > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        yPos = margin;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Notes:', margin, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      const notesText = notesDiv.textContent.replace(/Notes:\s*/i, '').trim();
      const notesLines = doc.splitTextToSize(notesText, maxWidth);
      notesLines.forEach((line) => {
        if (yPos > doc.internal.pageSize.getHeight() - 30) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin, yPos);
        yPos += 6;
      });
      yPos += 5;
      return;
    }

    if (block.type === 'text') {
      if (block.text === '\n') {
        yPos += 5;
      } else if (block.text.trim()) {
        addText(block.text.trim(), block.fontSize || 11, block.isBold, block.align || 'left');
      }
    }
  });

  return doc;
};

/** Download PDF using same styled HTML+CSS as Print (canonical renderer + inlined CSS). */
export const downloadPDF = async (document, client, type = 'invoice') => {
  const fileName = `${type}_${document.invoice_number || document.id}.pdf`;
  await generateStyledPDF(document, client, type);
  return fileName;
};
